import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Truck, Camera, X as XIcon, AlertTriangle, Loader2, MapPin, RefreshCw, Link2, Satellite, BarChart3 } from "lucide-react";
import { uploadToCloudinary, cloudinaryThumb } from "@/lib/cloudinary";
import { toast } from "sonner";

const TIPOS = ["Furgoneta", "Furgón", "Camioneta", "Coche", "Otro"];
const ESTADOS = {
  disponible:    { label: "Disponible",    badge: "bg-emerald-500/20 text-emerald-300" },
  en_taller:     { label: "En taller",     badge: "bg-amber-500/20 text-amber-300" },
  no_disponible: { label: "No disponible", badge: "bg-zinc-700/40 text-zinc-400" },
};

const EMPTY_VEH = {
  nombre: "", matricula: "", tipo: "Furgoneta", marca_modelo: "",
  capacidad_m3: "", propietario: "DNF", estado: "disponible",
  notas: "", km_actuales: "",
  foto_url: "", coste_km: "", consumo_litros_100km: "",
  proxima_itv: "", vencimiento_seguro: "",
  asegurado_con: "", numero_poliza: "",
  proxima_revision: "", km_proxima_revision: "",
  intervalo_mantenimiento_km: "",
};

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.floor((d - new Date()) / 86400000);
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const sec = Math.floor((new Date() - d) / 1000);
  if (sec < 60) return `hace ${sec}s`;
  if (sec < 3600) return `hace ${Math.floor(sec / 60)}min`;
  if (sec < 86400) return `hace ${Math.floor(sec / 3600)}h`;
  return `hace ${Math.floor(sec / 86400)}d`;
}

export default function Vehiculos() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY_VEH);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [saving, setSaving] = useState(false);
  // GPS Wialon
  const [wialonUnits, setWialonUnits] = useState([]);
  const [wialonLoading, setWialonLoading] = useState(true);
  const [wialonError, setWialonError] = useState(null);
  const [openVincular, setOpenVincular] = useState(null); // unit object

  const load = async () => {
    const { data } = await api.get("/vehiculos");
    setItems(data);
  };
  const loadWialon = async (force = false) => {
    setWialonLoading(true);
    try {
      const { data } = await api.get(`/wialon/vehiculos${force ? "?force=true" : ""}`);
      setWialonUnits(data);
      setWialonError(null);
    } catch (e) {
      setWialonError(e?.response?.data?.detail || e.message);
    } finally { setWialonLoading(false); }
  };
  useEffect(() => {
    load();
    loadWialon();
    const t = setInterval(() => loadWialon(false), 60000); // refresco cada 60s
    return () => clearInterval(t);
  }, []);

  const openNew = () => { setEdit(null); setForm(EMPTY_VEH); setOpen(true); };
  const openEdit = (v) => {
    setEdit(v);
    setForm({
      ...EMPTY_VEH, ...v,
      capacidad_m3: v.capacidad_m3 ?? "",
      km_actuales: v.km_actuales ?? "",
      coste_km: v.coste_km ?? "",
      consumo_litros_100km: v.consumo_litros_100km ?? "",
      foto_url: v.foto_url || "",
      proxima_itv: v.proxima_itv || "",
      vencimiento_seguro: v.vencimiento_seguro || "",
      asegurado_con: v.asegurado_con || "",
      numero_poliza: v.numero_poliza || "",
      proxima_revision: v.proxima_revision || "",
      km_proxima_revision: v.km_proxima_revision || "",
      intervalo_mantenimiento_km: v.intervalo_mantenimiento_km || "",
      notas: v.notas || "",
      matricula: v.matricula || "",
      marca_modelo: v.marca_modelo || "",
    });
    setOpen(true);
  };

  const onFotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Imagen demasiado grande (máx 10 MB)");
      return;
    }
    setUploadingFoto(true);
    try {
      const { secure_url } = await uploadToCloudinary(file, "dnf/vehiculos", "image");
      setForm((f) => ({ ...f, foto_url: secure_url }));
      toast.success("Foto subida");
    } catch (err) {
      toast.error(err.message || "Error al subir foto");
    } finally {
      setUploadingFoto(false);
      e.target.value = "";
    }
  };

  const saveVehiculo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const p = { ...form };
      p.capacidad_m3 = p.capacidad_m3 ? parseFloat(p.capacidad_m3) : null;
      p.km_actuales = p.km_actuales ? parseFloat(p.km_actuales) : null;
      p.coste_km = p.coste_km !== "" && p.coste_km !== null ? parseFloat(p.coste_km) : null;
      p.consumo_litros_100km = p.consumo_litros_100km !== "" && p.consumo_litros_100km !== null ? parseFloat(p.consumo_litros_100km) : null;
      p.activo = p.estado !== "no_disponible";
      p.km_proxima_revision = p.km_proxima_revision !== "" && p.km_proxima_revision !== null
        ? parseFloat(p.km_proxima_revision) : null;
      p.intervalo_mantenimiento_km = p.intervalo_mantenimiento_km !== "" && p.intervalo_mantenimiento_km !== null
        ? parseFloat(p.intervalo_mantenimiento_km) : null;
      ["proxima_itv", "vencimiento_seguro", "asegurado_con", "numero_poliza",
       "proxima_revision",
       "marca_modelo", "matricula", "notas", "foto_url"].forEach((k) => {
        if (p[k] === "") p[k] = null;
      });
      if (edit) await api.patch(`/vehiculos/${edit.id}`, p);
      else await api.post("/vehiculos", p);
      toast.success(edit ? "Vehículo actualizado" : "Vehículo creado");
      setOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const deleteVeh = async (v) => {
    if ((v.rutas_activas || 0) > 0) {
      toast.error(`No se puede borrar: ${v.rutas_activas} ruta(s) activa(s)`);
      return;
    }
    if (!window.confirm(`¿Eliminar ${v.nombre}?`)) return;
    await api.delete(`/vehiculos/${v.id}`);
    toast.success("Vehículo eliminado");
    load();
  };

  const vincularVehiculo = async (avl_id, vehiculo_id) => {
    try {
      await api.post("/wialon/vincular", { avl_id, vehiculo_id });
      toast.success("Vehículo vinculado al GPS");
      setOpenVincular(null);
      await Promise.all([load(), loadWialon(true)]);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const desvincularVehiculo = async (vehiculo_id) => {
    if (!window.confirm("¿Quitar la vinculación con el GPS?")) return;
    try {
      await api.post(`/wialon/desvincular/${vehiculo_id}`);
      toast.success("Desvinculado");
      await Promise.all([load(), loadWialon(true)]);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader title="Flota de vehículos"
        subtitle="Vehículos disponibles con seguro, ITV, capacidad y costes · GPS en vivo Wialon" />

      <div className="grid grid-cols-1 gap-5">

        {/* GPS EN VIVO (Wialon) */}
        <div className="bg-gradient-to-br from-blue-900/20 to-zinc-900/40 border border-blue-500/30 rounded-xl p-4" data-testid="gps-vivo-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-sm font-semibold flex items-center gap-2">
              <Satellite className="w-4 h-4 text-blue-300" />
              GPS en vivo
              {wialonLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />}
              {wialonError && <span className="text-[11px] text-red-400">⚠ {wialonError}</span>}
              <span className="text-[11px] text-zinc-500 font-normal">({wialonUnits.length} vehículos · refresco cada 60s)</span>
            </div>
            <Button size="sm" onClick={() => loadWialon(true)} variant="ghost"
              data-testid="btn-refresh-wialon"
              className="h-7 text-blue-300 hover:bg-blue-500/10 text-xs">
              <RefreshCw className="w-3 h-3 mr-1" /> Refrescar
            </Button>
          </div>
          {wialonUnits.length === 0 && !wialonLoading && (
            <div className="text-xs text-zinc-500 italic">Sin vehículos detectados. Verifica el token Wialon en .env.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {wialonUnits.map((u) => {
              const movement = u.velocidad_kmh > 5 ? "⚡ moviéndose" : u.velocidad_kmh > 0 ? "🐢 lento" : "🅿️ parado";
              const movColor = u.velocidad_kmh > 5 ? "text-emerald-300" : u.velocidad_kmh > 0 ? "text-amber-300" : "text-zinc-400";
              return (
                <div key={u.avl_id} data-testid={`wialon-unit-${u.avl_id}`}
                  className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-2.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Truck className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-zinc-100 text-sm truncate">{u.nombre}</div>
                      <div className="text-[10px] text-zinc-500 font-mono">AVL {u.avl_id}</div>
                    </div>
                    {u.vehiculo_dnf ? (
                      <span className="text-[10px] bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded">
                        ↔ {u.vehiculo_dnf.alias || u.vehiculo_dnf.matricula}
                      </span>
                    ) : (
                      <Button size="sm" onClick={() => setOpenVincular(u)} variant="ghost"
                        data-testid={`btn-vincular-${u.avl_id}`}
                        className="h-6 px-1.5 text-[10px] text-zinc-400 hover:text-blue-300">
                        <Link2 className="w-2.5 h-2.5 mr-0.5" /> Vincular
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className={`font-semibold ${movColor}`}>
                      {movement} {u.velocidad_kmh > 0 ? `${u.velocidad_kmh} km/h` : ""}
                    </span>
                    <span className="text-zinc-500">{timeAgo(u.ultima_posicion_at)}</span>
                  </div>
                  {u.lat && u.lng && (
                    <a href={`https://www.google.com/maps?q=${u.lat},${u.lng}`} target="_blank" rel="noreferrer"
                      data-testid={`btn-mapa-${u.avl_id}`}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] text-blue-300 hover:text-blue-200">
                      <MapPin className="w-2.5 h-2.5" /> Ver en Google Maps
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* VEHÍCULOS */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold flex items-center gap-2">
              <Truck className="w-4 h-4 text-amber-400" /> Vehículos ({items.length})
            </div>
            <Button size="sm" onClick={openNew} data-testid="new-vehiculo-btn"
              className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 h-8 px-2.5 text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo
            </Button>
          </div>

          <div className="space-y-2" data-testid="vehiculos-list">
            {items.map((v) => {
              const est = ESTADOS[v.estado] || ESTADOS.disponible;
              const itvDays = daysUntil(v.proxima_itv);
              const segDays = daysUntil(v.vencimiento_seguro);
              return (
                <div key={v.id} data-testid={`vehiculo-${v.id}`}
                  className="flex items-stretch gap-3 bg-zinc-950/60 border border-zinc-800 rounded-lg p-2 hover:border-zinc-700 transition-colors">
                  <div className="w-24 h-20 rounded overflow-hidden bg-zinc-900 flex items-center justify-center shrink-0">
                    {v.foto_url ? (
                      <img src={cloudinaryThumb(v.foto_url, 200, 160)} alt={v.nombre} className="w-full h-full object-cover" />
                    ) : (
                      <Truck className="w-7 h-7 text-zinc-700" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-zinc-100">{v.nombre}</span>
                      {v.matricula && <span className="text-xs text-zinc-500 font-mono">{v.matricula}</span>}
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${est.badge}`}>{est.label}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {v.marca_modelo || v.tipo} · {v.propietario}
                      {v.capacidad_m3 ? ` · ${v.capacidad_m3} m³` : ""}
                      {v.km_actuales ? ` · ${v.km_actuales.toLocaleString()} km` : ""}
                      {v.coste_km ? ` · ${v.coste_km.toFixed(2)} €/km` : ""}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {(v.rutas_activas > 0 || v.rutas_total > 0) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300">
                          {v.rutas_activas || 0} ruta(s) activa(s) · {v.rutas_total || 0} totales
                        </span>
                      )}
                      {itvDays !== null && itvDays < 30 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${itvDays < 0 ? "bg-red-500/15 text-red-300" : "bg-orange-500/15 text-orange-300"}`}>
                          <AlertTriangle className="w-2.5 h-2.5" /> ITV {itvDays < 0 ? `vencida ${-itvDays}d` : `en ${itvDays}d`}
                        </span>
                      )}
                      {segDays !== null && segDays < 30 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${segDays < 0 ? "bg-red-500/15 text-red-300" : "bg-orange-500/15 text-orange-300"}`}>
                          <AlertTriangle className="w-2.5 h-2.5" /> Seguro {segDays < 0 ? `vencido ${-segDays}d` : `en ${segDays}d`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 justify-center">
                    <Link to={`/vehiculos/${v.id}`} data-testid={`detalle-vehiculo-${v.id}`}
                      title="Ver KPIs y partes" className="h-7 w-7 inline-flex items-center justify-center rounded text-zinc-400 hover:text-amber-300 hover:bg-zinc-900">
                      <BarChart3 className="w-3.5 h-3.5" />
                    </Link>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`edit-vehiculo-${v.id}`}
                      className="h-7 w-7 p-0 text-zinc-400 hover:text-amber-300"><Pencil className="w-3.5 h-3.5" /></Button>
                    {v.wialon_avl_id && (
                      <Button size="sm" variant="ghost" onClick={() => desvincularVehiculo(v.id)}
                        title={`Vinculado a Wialon AVL ${v.wialon_avl_id}`}
                        data-testid={`unlink-${v.id}`}
                        className="h-7 w-7 p-0 text-blue-400 hover:text-red-400"><Satellite className="w-3.5 h-3.5" /></Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteVeh(v)}
                      className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              );
            })}
            {items.length === 0 && <div className="text-xs text-zinc-500 italic text-center py-6">Aún no hay vehículos. Pulsa "Nuevo" para añadir tu primera furgo.</div>}
          </div>
        </div>
      </div>

      {/* Dialog Vehículo */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? `Editar: ${edit.nombre}` : "Nuevo vehículo"}</DialogTitle></DialogHeader>
          <form onSubmit={saveVehiculo} className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Nombre / alias *</Label>
              <Input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Furgo Blanca"
                className="bg-zinc-900 border-zinc-800" data-testid="form-vehiculo-nombre" /></div>
            <div><Label className="text-xs">Matrícula</Label>
              <Input value={form.matricula} onChange={(e) => setForm({ ...form, matricula: e.target.value.toUpperCase() })}
                placeholder="1234-ABC" className="bg-zinc-900 border-zinc-800" /></div>

            <div><Label className="text-xs">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Marca y modelo</Label>
              <Input value={form.marca_modelo} onChange={(e) => setForm({ ...form, marca_modelo: e.target.value })}
                placeholder="VW Transporter T6" className="bg-zinc-900 border-zinc-800" /></div>

            <div className="col-span-2">
              <Label className="text-xs">Estado</Label>
              <div className="grid grid-cols-3 gap-1.5 mt-1" data-testid="estado-vehiculo">
                {Object.entries(ESTADOS).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => setForm({ ...form, estado: k })}
                    data-testid={`estado-${k}`}
                    className={`text-xs py-2 rounded border transition-colors ${
                      form.estado === k
                        ? "bg-amber-500/20 text-amber-200 border-amber-500/40"
                        : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700"
                    }`}>{v.label}</button>
                ))}
              </div>
            </div>

            <div className="col-span-2">
              <Label className="text-xs">Foto</Label>
              <div className="mt-1">
                {form.foto_url ? (
                  <div className="relative w-full max-w-sm rounded overflow-hidden bg-zinc-900 border border-zinc-800">
                    <img src={form.foto_url} alt="vehiculo" className="w-full max-h-56 object-contain" />
                    <button type="button" onClick={() => setForm({ ...form, foto_url: "" })}
                      className="absolute top-1.5 right-1.5 bg-zinc-950/80 rounded-full p-1 hover:bg-red-500/80">
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 w-full max-w-sm h-32 border-2 border-dashed border-zinc-800 rounded-lg cursor-pointer hover:border-amber-500/50 text-xs text-zinc-500 hover:text-amber-300">
                    {uploadingFoto ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                    {uploadingFoto ? "Subiendo..." : "Sube una foto"}
                    <input type="file" accept="image/*" onChange={onFotoChange} className="hidden" data-testid="foto-input" />
                  </label>
                )}
              </div>
            </div>

            <div><Label className="text-xs">Capacidad m³</Label>
              <Input type="number" step="0.1" value={form.capacidad_m3} onChange={(e) => setForm({ ...form, capacidad_m3: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            <div><Label className="text-xs">Propietario</Label>
              <Select value={form.propietario} onValueChange={(v) => setForm({ ...form, propietario: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="DNF">DNF</SelectItem>
                  <SelectItem value="alquiler">Alquiler</SelectItem>
                  <SelectItem value="externo">Externo</SelectItem>
                </SelectContent>
              </Select></div>

            <div><Label className="text-xs">Km actuales</Label>
              <Input type="number" value={form.km_actuales} onChange={(e) => setForm({ ...form, km_actuales: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            <div><Label className="text-xs">Consumo (L/100km)</Label>
              <Input type="number" step="0.1" value={form.consumo_litros_100km}
                data-testid="input-consumo"
                onChange={(e) => setForm({ ...form, consumo_litros_100km: e.target.value })}
                placeholder="7.5" className="bg-zinc-900 border-zinc-800" /></div>
            <div><Label className="text-xs">Coste por km (€) <span className="text-zinc-500">override opcional</span></Label>
              <Input type="number" step="0.01" value={form.coste_km} onChange={(e) => setForm({ ...form, coste_km: e.target.value })} placeholder="0.35" className="bg-zinc-900 border-zinc-800" /></div>

            <div><Label className="text-xs">Próxima ITV</Label>
              <Input type="date" value={form.proxima_itv} onChange={(e) => setForm({ ...form, proxima_itv: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            <div><Label className="text-xs">Vencimiento seguro</Label>
              <Input type="date" value={form.vencimiento_seguro} onChange={(e) => setForm({ ...form, vencimiento_seguro: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>

            <div><Label className="text-xs">Asegurado con</Label>
              <Input value={form.asegurado_con} onChange={(e) => setForm({ ...form, asegurado_con: e.target.value })} placeholder="Mapfre / Línea Directa..." className="bg-zinc-900 border-zinc-800" /></div>
            <div><Label className="text-xs">Nº de póliza</Label>
              <Input value={form.numero_poliza} onChange={(e) => setForm({ ...form, numero_poliza: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>

            <div><Label className="text-xs">Próxima revisión taller</Label>
              <Input type="date" value={form.proxima_revision} onChange={(e) => setForm({ ...form, proxima_revision: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            <div><Label className="text-xs">Km próxima revisión</Label>
              <Input type="number" step="100" value={form.km_proxima_revision} onChange={(e) => setForm({ ...form, km_proxima_revision: e.target.value })} placeholder="ej. 120.000" className="bg-zinc-900 border-zinc-800" /></div>

            <div className="col-span-2">
              <Label className="text-xs">Intervalo de mantenimiento (km)</Label>
              <Input type="number" step="500" value={form.intervalo_mantenimiento_km}
                onChange={(e) => setForm({ ...form, intervalo_mantenimiento_km: e.target.value })}
                placeholder="ej. 15.000 — cada cuántos km toca revisión"
                className="bg-zinc-900 border-zinc-800" data-testid="form-vehiculo-intervalo" />
              <div className="text-[10px] text-zinc-500 mt-0.5">Si lo configuras, calcularemos automáticamente la próxima revisión y te avisaremos 1.000 km antes.</div>
            </div>

            <div className="col-span-2"><Label className="text-xs">Observaciones</Label>
              <Textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>

            <DialogFooter className="col-span-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-zinc-400">Cancelar</Button>
              <Button type="submit" disabled={saving || uploadingFoto} data-testid="save-vehiculo-btn"
                className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Vincular GPS */}
      <Dialog open={!!openVincular} onOpenChange={(o) => !o && setOpenVincular(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="dialog-vincular-wialon">
          <DialogHeader>
            <DialogTitle className="text-blue-300 flex items-center gap-2">
              <Satellite className="w-5 h-5" /> Vincular GPS al vehículo DNF
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-3">
              <div className="text-xs text-zinc-500">Unidad Wialon</div>
              <div className="font-semibold text-zinc-100">{openVincular?.nombre}</div>
              <div className="text-[11px] text-zinc-500 font-mono">AVL {openVincular?.avl_id}</div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">¿A qué vehículo DNF corresponde?</Label>
              <div className="mt-2 max-h-72 overflow-y-auto space-y-1">
                {items.length === 0 && (
                  <div className="text-xs text-zinc-500 italic">No hay vehículos DNF. Crea uno primero.</div>
                )}
                {items.map((v) => (
                  <button key={v.id}
                    onClick={() => openVincular && vincularVehiculo(openVincular.avl_id, v.id)}
                    data-testid={`pick-vehiculo-${v.id}`}
                    className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded p-2 transition-colors disabled:opacity-50"
                    disabled={!!v.wialon_avl_id && v.wialon_avl_id !== openVincular?.avl_id}>
                    <div className="flex items-center gap-2">
                      <Truck className="w-3.5 h-3.5 text-amber-400" />
                      <span className="font-semibold text-zinc-100 text-sm">{v.nombre}</span>
                      {v.matricula && <span className="text-[11px] text-zinc-500 font-mono">{v.matricula}</span>}
                      {v.wialon_avl_id && (
                        <span className="text-[10px] bg-blue-500/15 text-blue-300 px-1.5 py-0.5 rounded ml-auto">
                          AVL {v.wialon_avl_id}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenVincular(null)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
